import React, { useState, useEffect } from "react";
import {
  createStyles,
  FormHelperText,
  Grid,
  makeStyles,
  Theme
} from "@material-ui/core";
import {
  CheckboxWithLabel,
  DropdownWithLabel,
  FolderWithLabel,
  NumberFieldWithLabel,
  SimpleDialog,
  memoWithOpen,
} from "../../components";
import { pathWithExtension, useAssetTools } from "../../utilities";
import path from "path";
import LocalScene from "../../viewer/js/projections";
import { useTaskQueue } from "../../hooks";
import { useTranslation } from "react-i18next";
import { FunctionProps } from "./props";

enum ChannelType {
  Empty="empty",
  Red="red",
  Green="green",
  Blue="blue",
  Linearity="linearity",
  Planarity="planarity",
  Scattering="scattering",
  Normal="normal",
  Intensity="intensity",
  NumReturns="num_returns",
  ReturnNumber="return_number",
  Classification="classification"
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    helperText: {
      textAlign: "center",
      paddingTop: theme.spacing(2)
    }
  }),
);

interface ChannelSelectorProps {
  title: string;
  value: string | number;
  onChange: React.Dispatch<any>;
}

const ChannelSelector = (props: ChannelSelectorProps) => {
  const {title, value, onChange} = props;

  const {t} = useTranslation();

  return (
    <DropdownWithLabel
      title={title}
      label={t('functions.las_to_orthomosaic.the_value_that_will_be_used_for_this_color_channel')}
      value={value}
      onChange={onChange}
      options={[
        [ChannelType.Empty, t('functions.las_to_orthomosaic.empty')],
        [ChannelType.Red, t('functions.las_to_orthomosaic.red')],
        [ChannelType.Green, t('functions.las_to_orthomosaic.green')],
        [ChannelType.Blue, t('functions.las_to_orthomosaic.blue')],
        [ChannelType.Linearity, t('functions.las_to_orthomosaic.linearity')],
        [ChannelType.Planarity, t('functions.las_to_orthomosaic.planarity')],
        [ChannelType.Scattering, t('functions.las_to_orthomosaic.scattering')],
        [ChannelType.Normal, t('functions.las_to_orthomosaic.normals')],
        [ChannelType.Intensity, t('functions.las_to_orthomosaic.intensity')],
        [ChannelType.NumReturns, t('functions.las_to_orthomosaic.number_of_returns')],
        [ChannelType.ReturnNumber, t('functions.las_to_orthomosaic.return_number')],
        [ChannelType.Classification, t('functions.las_to_orthomosaic.classification')]
      ]}
    />
  );
};

export const LasToOrthomosaic = memoWithOpen((props: FunctionProps) => {
  const {assets, open, onClose} = props;

  const classes = useStyles();
  const {addRunningTask} = useTaskQueue();
  const {addAssetPaths} = useAssetTools();
  const {t} = useTranslation();

  const defaultPixelSize = {value: 0.1, error: false};

  const [outputPath, setOutputPath] = useState(null);
  const [channelOne, setChannelOne] = useState(ChannelType.Empty);
  const [channelTwo, setChannelTwo] = useState(ChannelType.Empty);
  const [channelThree, setChannelThree] = useState(ChannelType.Empty);
  const [pixelSize, setPixelSize] = useState(defaultPixelSize);
  const [importResult, setImportResult] = useState(true);

  // TODO: actually handle multiple inputs (sent multiple exe commands to processing queue)
  const asset = assets ? assets[0] : undefined;

  const resetDefaults = () => {
    setOutputPath(null);
  };

  const handleSubmit = () => {
    const pythonName = "las_to_orthomosaic";
    const tifPath = pathWithExtension(
      path.join(outputPath, asset.name), "tif");

    let commands = [
      "-p", pythonName,
      "--input_path", asset.path,
      "--output_path", outputPath,
      "--channel_1", channelOne,
      "--channel_2", channelTwo,
      "--channel_3", channelThree,
      "--pixel_size", pixelSize.value,
      "--units", LocalScene.dataProjectionUnits
    ];

    const onFinish = async () => {
      if (!importResult) return;

      // Add new orthmosaic
      await addAssetPaths([tifPath], asset.folderID);
    };

    onClose();
    addRunningTask(commands, onFinish);
  };

  useEffect(() => {
    if (open) return;
    resetDefaults();
  }, [open]);

  const emptyChannels = (channelOne === ChannelType.Empty)
    && (channelTwo === ChannelType.Empty)
    && (channelThree === ChannelType.Empty);

  const canSubmit = (outputPath !== null)
    && !pixelSize.error
    && !emptyChannels;

  return (
    <React.Fragment>
      <SimpleDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        title={t('functions.las_to_orthomosaic.title')}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Channel 1 color */}
          <Grid item xs={6}>
            <ChannelSelector
              title={t('functions.las_to_orthomosaic.channel_1_value')}
              value={channelOne}
              onChange={setChannelOne}
            />
          </Grid>

          {/** Channel 2 color */}
          <Grid item xs={6}>
            <ChannelSelector
              title={t('functions.las_to_orthomosaic.channel_2_value')}
              value={channelTwo}
              onChange={setChannelTwo}
            />
          </Grid>

          {/** Channel 3 color */}
          <Grid item xs={6}>
            <ChannelSelector
              title={t('functions.las_to_orthomosaic.channel_3_value')}
              value={channelThree}
              onChange={setChannelThree}
            />
          </Grid>

          {/** Pixel size */}
          <Grid item xs={6}>
            <NumberFieldWithLabel
              title={t('functions.las_to_orthomosaic.pixel_size')}
              label={t('functions.las_to_orthomosaic.the_physical_size_of_each_pixel')}
              data={pixelSize}
              setData={setPixelSize}
              min={0}
              max={20}
              step={0.05}
            />
          </Grid>

          {/** Output folder */}
          <Grid item xs={6}>
            <FolderWithLabel
              title={t('point-markup.output-folder')}
              label={t('functions.las_to_orthomosaic.output_folder_for_orthomosaic_images')}
              value={outputPath}
              onChange={setOutputPath}
            />
          </Grid>

          {/** Import results */}
          <Grid item xs={6}>
            <CheckboxWithLabel
              title={t('functions.import_results')}
              label={t('functions.las_to_orthomosaic.generated_orthomosaic_images_will_be_imported_into_the_viewer')}
              value={importResult}
              onChange={setImportResult}
            />
          </Grid>

        </Grid>

        {emptyChannels && <FormHelperText className={classes.helperText} error>
          {t('functions.las_to_orthomosaic.at_least_one_channel_must_be')}
        </FormHelperText>}
      </SimpleDialog>
    </React.Fragment>
  );
});
